import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Copy as CopyIcon, Wand2 } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { toast } from "@/shared/hooks/use-toast";
import { posterProviderLabel } from "../../../model/providerLabels";
import { sizeSummary } from "../../../model/detailShape";
import { USE_CASES, WIDE_LAYOUTS } from "../../../longimage/model/blocks";
import type { PosterTaskDto } from "../../../api/posterService";
import { promptForRecreate } from "../../../model/free-create/recreatePrompt";

/** 清晰度档位(canvas.scale)→ 展示文案;跟 resolutionTier 一样是语言中立的字母数字,不进 i18n。 */
const SCALE_LABELS: Record<number, string> = { 1: "1K", 2: "2K", 3: "4K" };

/** 背景档位(canvas.background)—— 照抄长图搭建器 WholeImagePanel.tsx 里这三档的现有中文文案
 *  (该面板整个不走 i18n,值本身就是模型层中文常量,不翻译)。 */
const BACKGROUND_LABELS: Record<string, string> = { white: "白", gray: "浅灰", brand: "品牌色" };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * 长图创建参数逐行展开(2026-09-11,老板原话「详情页看不到具体的风格选项什么的」)。
 *
 * 入参是建这一稿时发给引擎的整包 payload(见 `useLongImageSpec().raw`),**库里存的 JSON,
 * 存量稿 / 半截稿都可能不合形状** —— 逐项防御性解析,某一项类型不对就跳过那一行,
 * 不让一个坏字段拖垮整块参数展示(同 `useLongImageSpec.ts` 头注「不半份恢复」的精神,
 * 但这里颗粒度是「行」不是「块」:少一行没关系,不能因为一行解析失败整块参数区消失)。
 */
function buildLongImageRows(
  longImage: Record<string, unknown> | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): Array<{ key: string; label: string; value: string }> {
  if (!isRecord(longImage)) return [];
  const rows: Array<{ key: string; label: string; value: string }> = [];

  const canvas = longImage.canvas;
  if (isRecord(canvas)) {
    const useCase = canvas.useCase;
    if (typeof useCase === "string") {
      const found = USE_CASES.find((u) => u.key === useCase);
      if (found) {
        rows.push({ key: "useCase", label: t("taskDetail.longImageUseCase"), value: found.label });
      }
    }

    const direction = canvas.direction;
    if (direction === "v" && typeof canvas.width === "number") {
      rows.push({
        key: "canvas",
        label: t("taskDetail.longImageCanvas"),
        value: t("taskDetail.longImageVertical", { width: canvas.width }),
      });
    } else if (
      direction === "h" &&
      typeof canvas.wideWidth === "number" &&
      typeof canvas.wideHeight === "number"
    ) {
      rows.push({
        key: "canvas",
        label: t("taskDetail.longImageCanvas"),
        value: t("taskDetail.longImageHorizontal", {
          width: canvas.wideWidth,
          height: canvas.wideHeight,
        }),
      });
      const wideLayout = canvas.wideLayout;
      if (typeof wideLayout === "string") {
        const found = WIDE_LAYOUTS.find((l) => l.key === wideLayout);
        if (found) {
          rows.push({ key: "layout", label: t("taskDetail.longImageLayout"), value: found.label });
        }
      }
    }

    const scale = canvas.scale;
    if (typeof scale === "number" && SCALE_LABELS[scale]) {
      rows.push({
        key: "resolution",
        label: t("taskDetail.longImageResolution"),
        value: SCALE_LABELS[scale],
      });
    }

    const background = canvas.background;
    if (typeof background === "string" && BACKGROUND_LABELS[background]) {
      rows.push({
        key: "background",
        label: t("taskDetail.longImageBackground"),
        value: BACKGROUND_LABELS[background],
      });
    }
  }

  const style = longImage.style;
  if (isRecord(style) && typeof style.label === "string" && style.label) {
    const tone = typeof style.tone === "string" && style.tone ? style.tone : null;
    rows.push({
      key: "style",
      label: t("taskDetail.style"),
      value: tone ? `${style.label} · ${tone}` : style.label,
    });
  }

  const blocks = longImage.blocks;
  if (Array.isArray(blocks)) {
    const visible = blocks.filter((b) => !(isRecord(b) && b.hidden === true)).length;
    rows.push({
      key: "blockCount",
      label: t("taskDetail.longImageBlockCount"),
      value: t("taskDetail.longImageBlockCountUnit", { n: visible }),
    });
  }

  return rows;
}

/**
 * 「本次设置」折叠区(2026-07-28 视觉舱精调 W2)。
 *
 * ═══ 为什么从整张大卡降级成折叠区 ══════════════════════════════════════════
 * 它以前是页面第三块、占满整宽的 Card:提示词全文 + 引擎/尺寸/张数四列 + 两个按钮,
 * 连同上面的页头和状态条把首屏 ~550px 全吃掉了,**用户花积分生成的图被推到第二屏**。
 * 用户原话「头轻脚重」。
 *
 * 它是**溯源信息**不是操作信息 —— 看图时不需要,想复现时才需要。所以:
 *   收起态 = 一行摘要(9:16 + 1:1 + 16:9 · 1K · GPT · 3 张),想看细节点开
 *   展开态 = 提示词全文 + 复制 + 逐项参数 + 「用此提示词重新创作」
 *
 * ═══ 2026-09-11 两处补料 ════════════════════════════════════════════════════
 *   longImage prop  = 长图创建整包 payload(`useLongImageSpec().raw`),老板反馈
 *                     「看不到具体的风格选项什么的」—— 逐项解析成参数网格追加行。
 *   task.coverImageUrl = 图改图(i2i)参考原图,老板反馈「原图也要显示的吧」——
 *                     收起态露缩略图提示「这单带原图」,展开态给完整块可点开原尺寸。
 */
export function TaskSettingsFold({
  task,
  onRecreate,
  longImage,
  className,
}: {
  task: PosterTaskDto;
  onRecreate: () => void;
  /** 长图创建整包 payload;非长图详情 / 拿不到就传 null,不渲染长图专属行。 */
  longImage?: Record<string, unknown> | null;
  className?: string;
}) {
  const { t } = useTranslation("poster");
  const [open, setOpen] = useState(false);
  // 1006:「本次提示词」对客只露用户原文(新单 userPrompt;老单从 rawPrompt 剥【画幅构图】【产品】等系统段)
  const prompt = promptForRecreate(task) || undefined;
  const engine = posterProviderLabel(task.provider);
  const summary = [sizeSummary(task), engine, t("freeCreate.countUnit", { n: task.requestedCount })]
    .filter(Boolean)
    .join(" · ");

  const longImageRows = buildLongImageRows(longImage, t);
  const hasLongImageStyle = longImageRows.some((r) => r.key === "style");
  const coverImageUrl = task.coverImageUrl;

  return (
    <div className={cn("rounded-lg border border-border/60 bg-card/40", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
        />
        {coverImageUrl && (
          // 收起态就露一个小缩略图 —— 不点开也知道「这单带原图」(老板反馈原话)。
          <img
            src={coverImageUrl}
            alt={t("taskDetail.sourceImage")}
            className="h-6 w-6 shrink-0 rounded object-cover"
            onError={(e) => {
              // presigned URL 可能已过期,拿不到图就悄悄隐藏,不留一个裂图标 icon。
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        <span className="font-medium">{t("taskDetail.settingsTitle")}</span>
        <span className="min-w-0 flex-1 truncate text-right text-muted-foreground" title={summary}>
          {summary}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-border/60 p-3 text-xs">
          {prompt && (
            <div className="rounded-md border border-border/60 bg-background/40 p-2.5">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-2xs uppercase tracking-wide text-muted-foreground">
                  {t("taskDetail.rawPrompt")}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 gap-1 px-1.5 text-2xs"
                  onClick={() => {
                    void navigator.clipboard.writeText(prompt);
                    toast({ title: t("taskDetail.copied") });
                  }}
                >
                  <CopyIcon className="h-3 w-3" />
                  {t("taskDetail.copy")}
                </Button>
              </div>
              <p className="whitespace-pre-wrap break-words leading-relaxed">{prompt}</p>
            </div>
          )}

          {coverImageUrl && (
            <div className="rounded-md border border-border/60 bg-background/40 p-2.5">
              <span className="mb-1.5 block text-2xs uppercase tracking-wide text-muted-foreground">
                {t("taskDetail.sourceImage")}
              </span>
              <a href={coverImageUrl} target="_blank" rel="noreferrer">
                <img
                  src={coverImageUrl}
                  alt={t("taskDetail.sourceImage")}
                  className="max-h-40 rounded-md object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </a>
              <p className="mt-1 text-2xs text-muted-foreground">
                {t("taskDetail.sourceImageHint")}
              </p>
            </div>
          )}

          {task.notes && task.notes.trim() && (
            <div className="rounded-md border border-primary/25 bg-primary/5 p-2.5">
              <span className="mb-1 block text-2xs font-medium uppercase tracking-wide text-primary">
                {t("taskDetail.note")}
              </span>
              <p className="whitespace-pre-wrap break-words leading-relaxed">{task.notes.trim()}</p>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Row label={t("taskDetail.engine")} value={engine} />
            <Row label={t("taskDetail.size")} value={sizeSummary(task)} />
            <Row label={t("taskDetail.count")} value={String(task.requestedCount)} />
            {task.styleName && !hasLongImageStyle && (
              <Row label={t("taskDetail.style")} value={task.styleName} />
            )}
            {longImageRows.map((r) => (
              <Row key={r.key} label={r.label} value={r.value} />
            ))}
            <Row
              label={t("taskDetail.submitted")}
              value={task.submittedAt?.slice(0, 19).replace("T", " ") ?? "-"}
            />
            {task.completedAt && (
              <Row
                label={t("taskDetail.completed")}
                value={task.completedAt.slice(0, 19).replace("T", " ")}
              />
            )}
          </dl>

          {prompt && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={onRecreate}
            >
              <Wand2 className="h-3 w-3" />
              {t("taskDetail.recreate")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="truncate font-mono text-2xs font-medium" title={value}>
        {value}
      </dd>
    </div>
  );
}
